import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, ChevronRight, Cpu, Zap, DollarSign, TrendingUp, ArrowUp, ArrowDown } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyUtils';
import type { ExchangeRates } from '../../utils/currencyUtils';

interface MinerTableProps {
  miners: any[];
  isLoading: boolean;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
  currency: string;
  exchangeRates: ExchangeRates;
}

export function MinerTable({ 
  miners, 
  isLoading, 
  sortField: initialSortField, 
  sortDirection: initialSortDirection, 
  onSort,
  currency,
  exchangeRates 
}: MinerTableProps) {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    field: initialSortField,
    direction: initialSortDirection
  });

  const handleSort = (field: string) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="w-4 h-4" /> : 
      <ArrowDown className="w-4 h-4" />;
  };

  const sortedMiners = React.useMemo(() => {
    return [...miners].sort((a, b) => {
      const multiplier = sortConfig.direction === 'asc' ? 1 : -1;
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * multiplier;
      }
      return ((aValue as number) - (bValue as number)) * multiplier;
    });
  }, [miners, sortConfig]);

  const handleMinerClick = (minerId: string) => {
    navigate(`/miner/${minerId}`);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-200 rounded-t-lg mb-4" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-20 bg-gray-100 mb-2" />
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th 
              className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => handleSort('name')}
            >
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4" />
                Miner {getSortIcon('name')}
              </div>
            </th>
            <th 
              className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => handleSort('hashrate')}
            >
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Hashrate {getSortIcon('hashrate')}
              </div>
            </th>
            <th 
              className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => handleSort('profit24')}
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Daily Profit {getSortIcon('profit24')}
              </div>
            </th>
            <th 
              className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => handleSort('power')}
            >
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Power Cost {getSortIcon('power')}
              </div>
            </th>
            <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedMiners.map((miner, index) => {
            // Create a unique key combining multiple properties
            const uniqueKey = `${miner.model}-${miner.manufacturer}-${miner.coin}-${index}`;
            
            return (
              <tr 
                key={uniqueKey}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handleMinerClick(miner.model)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Cpu className="w-5 h-5 text-blue-500" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{miner.name}</div>
                      <div className="text-sm text-gray-500">{miner.manufacturer}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {miner.hashrate} {miner.hashrateUnit}
                  </div>
                  <div className="text-sm text-gray-500">
                    Efficiency: {(miner.hashrate / miner.power).toFixed(3)} {miner.hashrateUnit}/W
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm font-medium ${
                    miner.profit24 >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(miner.profit24, currency, exchangeRates)}
                  </div>
                  <div className="text-sm text-gray-500">
                    ROI: {miner.profit24 > 0 ? `${(365 * miner.profit24 / 5000 * 100).toFixed(1)}%` : 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{miner.power}W</div>
                  <div className="text-sm text-gray-500">
                    {formatCurrency(miner.cost24, currency, exchangeRates)}/day
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMinerClick(miner.model);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Details
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}